import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { AIAgentConfigurationJson } from 'app/types/entities'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'

type UnifiedServiceRow = {
  name: string
  offered: boolean
  lowPrice: number
  highPrice: number
  consultPrice: number
}

export type UseAIAgentLocationSettingsReturn = {
  agentConfig: AIAgentConfigurationJson
  setAgentConfig: React.Dispatch<React.SetStateAction<AIAgentConfigurationJson>>
  initialAgentConfig: AIAgentConfigurationJson
  settingsV2Enabled: boolean
  primaryTrackingNumber: string | undefined
  setPrimaryTrackingNumber: React.Dispatch<React.SetStateAction<string | undefined>>
  businessPhone: string | undefined
  setBusinessPhone: React.Dispatch<React.SetStateAction<string | undefined>>
  openHours: any
  setOpenHours: React.Dispatch<React.SetStateAction<any>>
  isDirtyForm: boolean
  setIsDirtyForm: React.Dispatch<React.SetStateAction<boolean>>
  unifiedServices: UnifiedServiceRow[]
  setUnifiedServices: React.Dispatch<React.SetStateAction<UnifiedServiceRow[]>>
  newUnifiedServiceRowIds: Set<number>
  newOfferRowIds: Set<number>
  dirtyFields: Set<string>
  markDirty: (fieldKey: string) => void
  getDirtyClass: (fieldKey: string) => string
  handleAddUnifiedService: () => void
  handleDeleteUnifiedService: (position: number) => void
  handleAddService: () => void
  handleAddServiceNotOffered: () => void
  handleLoadDefaults: () => void
  handleAddOffer: () => void
  handleDeleteService: (position: number) => void
  handleDeleteServiceNotOffered: (position: number) => Promise<void>
  handleDeleteOffer: (position: number) => void
}

export function useAIAgentLocationSettings(): UseAIAgentLocationSettingsReturn {
  const { locationId } = useParams()
  const [confirm] = useConfirmation()

  const [initialAgentConfig, setInitialAgentConfig] = useState<AIAgentConfigurationJson>({} as AIAgentConfigurationJson)
  const [agentConfig, setAgentConfig] = useState<AIAgentConfigurationJson>({} as AIAgentConfigurationJson)
  const [settingsV2Enabled, setSettingsV2Enabled] = useState(false)
  const [primaryTrackingNumber, setPrimaryTrackingNumber] = useState<string | undefined>()
  const [businessPhone, setBusinessPhone] = useState<string | undefined>()
  const [openHours, setOpenHours] = useState<any>()
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [unifiedServices, setUnifiedServices] = useState<UnifiedServiceRow[]>([])
  const [newUnifiedServiceRowIds, setNewUnifiedServiceRowIds] = useState<Set<number>>(new Set())
  const [newOfferRowIds, setNewOfferRowIds] = useState<Set<number>>(new Set())
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!locationId) return
    gqlFetchOne<any>(
      'aiAgentConfiguration',
      locationId,
      'id services { name lowPrice highPrice consultPrice } servicesNotOffered { name } settingsV2Enabled location { officeTransferNumber primaryTrackingNumber officeMainNumber businessPhone openHours }',
    )
      .then((data) => {
        const ac = data ?? {}
        const loc = (data as any)?.location ?? {}
        setInitialAgentConfig(ac)
        setAgentConfig(ac)
        setSettingsV2Enabled((data as any)?.settingsV2Enabled ?? false)
        setPrimaryTrackingNumber(loc.officeTransferNumber || loc.primaryTrackingNumber)
        setBusinessPhone(loc.officeMainNumber || loc.businessPhone)
        setOpenHours(loc.openHours)
        const offered: UnifiedServiceRow[] = (ac.services ?? []).map((s: any) => ({
          name: s.name,
          offered: true,
          lowPrice: s.lowPrice ?? 0,
          highPrice: s.highPrice ?? 0,
          consultPrice: s.consultPrice ?? 0,
        }))
        const notOffered: UnifiedServiceRow[] = (ac.servicesNotOffered ?? []).map((s: any) => ({
          name: s.name,
          offered: false,
          lowPrice: 0,
          highPrice: 0,
          consultPrice: 0,
        }))
        setUnifiedServices([...offered, ...notOffered])
      })
      .catch((err) => logError(`Error fetching AI agent location settings`, err))
  }, [locationId])

  const markDirty = (fieldKey: string) => {
    setDirtyFields((prev) => new Set(prev).add(fieldKey))
    setIsDirtyForm(true)
  }

  const getDirtyClass = (fieldKey: string) => {
    return dirtyFields.has(fieldKey) ? `border-2 !border-blue-600 rounded` : ``
  }

  const handleAddUnifiedService = () => {
    const newIndex = unifiedServices.length
    setUnifiedServices([...unifiedServices, { name: ``, offered: true, lowPrice: 0, highPrice: 0, consultPrice: 0 }])
    setNewUnifiedServiceRowIds((prev) => new Set(prev).add(newIndex))
    setIsDirtyForm(true)
  }

  const handleDeleteUnifiedService = (position: number) => {
    const item = unifiedServices[position]
    if (item.name !== ``) {
      confirm({
        title: `Delete Service`,
        description: `Are you sure you want to delete this service?`,
        variant: `danger`,
      })
        .then(() => {
          setUnifiedServices(unifiedServices.filter((_, i) => i !== position))
          setNewUnifiedServiceRowIds((prev) => {
            const next = new Set<number>()
            prev.forEach((id) => {
              if (id < position) next.add(id)
              else if (id > position) next.add(id - 1)
            })
            return next
          })
          setIsDirtyForm(true)
        })
        .catch(() => {})
    } else {
      setUnifiedServices(unifiedServices.filter((_, i) => i !== position))
      setNewUnifiedServiceRowIds((prev) => {
        const next = new Set<number>()
        prev.forEach((id) => {
          if (id < position) next.add(id)
          else if (id > position) next.add(id - 1)
        })
        return next
      })
    }
  }

  const handleAddService = () => {
    setAgentConfig({
      ...agentConfig,
      services: [
        ...(agentConfig?.services ?? []),
        { name: ``, lowPrice: 0, highPrice: 0, consultRequired: false, consultPrice: 0 },
      ],
    })
    setIsDirtyForm(true)
  }

  const handleAddServiceNotOffered = () => {
    setAgentConfig({
      ...agentConfig,
      servicesNotOffered: [
        ...(agentConfig?.servicesNotOffered ?? []),
        { name: ``, lowPrice: 0, highPrice: 0, consultRequired: false, consultPrice: 0 },
      ],
    })
  }

  const handleLoadDefaults = () => {
    const defaultServices: UnifiedServiceRow[] = [
      { name: `Cleaning`, offered: true, lowPrice: 100, highPrice: 200, consultPrice: 0 },
      { name: `Exam`, offered: true, lowPrice: 75, highPrice: 150, consultPrice: 0 },
      { name: `X-Ray`, offered: true, lowPrice: 50, highPrice: 250, consultPrice: 0 },
      { name: `Filling`, offered: true, lowPrice: 150, highPrice: 300, consultPrice: 0 },
      { name: `Root Canal`, offered: true, lowPrice: 800, highPrice: 1500, consultPrice: 0 },
      { name: `Crown`, offered: true, lowPrice: 800, highPrice: 1500, consultPrice: 0 },
      { name: `Extraction`, offered: true, lowPrice: 150, highPrice: 450, consultPrice: 0 },
      { name: `Emergency Exam`, offered: true, lowPrice: 100, highPrice: 200, consultPrice: 0 },
      { name: `Whitening`, offered: true, lowPrice: 75, highPrice: 500, consultPrice: 0 },
      { name: `Invisalign`, offered: true, lowPrice: 1500, highPrice: 7000, consultPrice: 0 },
      { name: `Implant`, offered: true, lowPrice: 1500, highPrice: 7000, consultPrice: 0 },
      { name: `Veneers`, offered: true, lowPrice: 250, highPrice: 3000, consultPrice: 0 },
      { name: `Bonding (per tooth)`, offered: true, lowPrice: 100, highPrice: 900, consultPrice: 0 },
    ]
    const newIds = new Set<number>()
    defaultServices.forEach((_, i) => newIds.add(i))
    setUnifiedServices(defaultServices)
    setNewUnifiedServiceRowIds(newIds)
    setIsDirtyForm(true)
  }

  const handleAddOffer = () => {
    const newIndex = (agentConfig?.offers ?? []).length
    setAgentConfig({
      ...agentConfig,
      offers: [
        ...(agentConfig?.offers ?? []),
        { name: ``, patientStatus: `New Patient`, eligibleInsurance: `Any`, onlyEmergencyEligible: false },
      ],
    })
    setNewOfferRowIds((prev) => new Set(prev).add(newIndex))
    setIsDirtyForm(true)
  }

  const handleDeleteService = (position: number) => {
    confirm({
      title: `Delete Service`,
      description: `Are you sure you want to delete this service?`,
      variant: `danger`,
    })
      .then(() => {
        setAgentConfig({
          ...agentConfig,
          services: agentConfig.services?.filter((_, i) => i !== position),
        })
        setIsDirtyForm(true)
      })
      .catch(() => {})
  }

  const handleDeleteServiceNotOffered = async (position: number) => {
    const itemToDelete = agentConfig.servicesNotOffered?.at(position)
    if (itemToDelete?.name !== ``) {
      await confirm({
        title: `Delete Service Not Offered`,
        description: `Are you sure you want to delete this service not offered?`,
        variant: `danger`,
      })
        .then(() => {
          setAgentConfig({
            ...agentConfig,
            servicesNotOffered: agentConfig.servicesNotOffered?.filter((_, i) => i !== position),
          })
          setIsDirtyForm(true)
        })
        .catch(() => {})
    } else {
      setAgentConfig({
        ...agentConfig,
        servicesNotOffered: agentConfig.servicesNotOffered?.filter((_, i) => i !== position),
      })
    }
  }

  const handleDeleteOffer = (position: number) => {
    confirm({
      title: `Delete Offer`,
      description: `Are you sure you want to delete this offer?`,
      variant: `danger`,
    })
      .then(() => {
        setAgentConfig({
          ...agentConfig,
          offers: agentConfig?.offers?.filter((_, i) => i !== position),
        })
        setNewOfferRowIds((prev) => {
          const next = new Set<number>()
          prev.forEach((id) => {
            if (id < position) next.add(id)
            else if (id > position) next.add(id - 1)
          })
          return next
        })
        setIsDirtyForm(true)
      })
      .catch(() => {})
  }

  return {
    agentConfig,
    setAgentConfig,
    initialAgentConfig,
    settingsV2Enabled,
    primaryTrackingNumber,
    setPrimaryTrackingNumber,
    businessPhone,
    setBusinessPhone,
    openHours,
    setOpenHours,
    isDirtyForm,
    setIsDirtyForm,
    unifiedServices,
    setUnifiedServices,
    newUnifiedServiceRowIds,
    newOfferRowIds,
    dirtyFields,
    markDirty,
    getDirtyClass,
    handleAddUnifiedService,
    handleDeleteUnifiedService,
    handleAddService,
    handleAddServiceNotOffered,
    handleLoadDefaults,
    handleAddOffer,
    handleDeleteService,
    handleDeleteServiceNotOffered,
    handleDeleteOffer,
  }
}
